import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db, auth } from './firebase'; // Ensure auth is imported!

export default function GenEdAssigner({ tenantId, studentId, initialAssigned = [] }) {
  const [genEdTeachers, setGenEdTeachers] = useState([]);
  const [assignedIds, setAssignedIds] = useState(initialAssigned);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [managerBuilding, setManagerBuilding] = useState('');

  // 1. Fetch the logged-in user's building, THEN fetch the filtered teachers
  useEffect(() => {
    const fetchBuildingAndTeachers = async () => {
      try {
        if (!auth.currentUser) return;

        // Step A: Get the Case Manager's Profile
        const userRef = doc(db, `districts/${tenantId}/users/${auth.currentUser.uid}`);
        const userSnap = await getDoc(userRef);
        
        if (!userSnap.exists()) {
          setIsLoading(false);
          return;
        }

        const userProfile = userSnap.data();
        const userSchool = userProfile.schoolId;
        setManagerBuilding(userSchool || 'District');

        // Step B: Query the Gen Ed Teachers
        const staffRef = collection(db, `districts/${tenantId}/users`);
        let q;

        // If they are assigned to a specific building, lock the query to that building.
        // If they are District Office or SuperAdmin, pull everyone.
        if (userSchool && userSchool !== 'District Office' && userProfile.role !== 'SuperAdmin') {
          q = query(
            staffRef, 
            where("role", "==", "Gen Ed"),
            where("schoolId", "==", userSchool)
          );
        } else {
          q = query(staffRef, where("role", "==", "Gen Ed"));
        }

        const snapshot = await getDocs(q);
        
        const teachers = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name || doc.data().email
        }));

        // Sort alphabetically by name
        teachers.sort((a, b) => a.name.localeCompare(b.name));
        setGenEdTeachers(teachers);
        setIsLoading(false);

      } catch (error) {
        console.error("Error fetching Gen Ed teachers:", error);
        setIsLoading(false);
      }
    };

    if (tenantId) fetchBuildingAndTeachers();
  }, [tenantId]);

  // 2. The Toggle Engine
  const handleToggleTeacher = async (teacherId) => {
    if (!studentId || !tenantId) return;
    setIsSaving(true);

    const studentRef = doc(db, `districts/${tenantId}/students/${studentId}`);
    const isCurrentlyAssigned = assignedIds.includes(teacherId);

    try {
      if (isCurrentlyAssigned) {
        await updateDoc(studentRef, {
          assignedGenEd: arrayRemove(teacherId)
        });
        setAssignedIds(prev => prev.filter(id => id !== teacherId));
      } else {
        await updateDoc(studentRef, {
          assignedGenEd: arrayUnion(teacherId)
        });
        setAssignedIds(prev => [...prev, teacherId]);
      }
    } catch (error) {
      console.error("Failed to update roster permissions:", error);
      alert("Permission denied. You may not have access to edit this student.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div style={{ fontSize: '13px', color: '#666' }}>Loading building roster...</div>;
  }

  return (
    <div style={{ backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '8px', border: '1px solid #e0e0e0', marginTop: '10px' }}>
      <h4 style={{ margin: '0 0 5px 0', color: '#003366', fontSize: '14px', display: 'flex', justifyContent: 'space-between' }}>
        <span>Assign General Ed Teachers</span>
        {isSaving && <span style={{ color: '#28a745', fontSize: '12px' }}>Saving...</span>}
      </h4>
      <p style={{ fontSize: '12px', color: '#666', marginBottom: '15px', marginTop: 0 }}>
        Showing teachers from: <strong>{managerBuilding}</strong>
      </p>

      {genEdTeachers.length === 0 ? (
        <div style={{ fontSize: '13px', color: '#ba0c2f', padding: '10px', backgroundColor: '#ffe6e6', borderRadius: '4px' }}>
          No General Ed teachers found in your building. 
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto', paddingRight: '5px' }}>
          {genEdTeachers.map((teacher) => {
            const isAssigned = assignedIds.includes(teacher.id);
            return (
              <label 
                key={teacher.id} 
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px', 
                  padding: '8px 12px', 
                  backgroundColor: isAssigned ? '#e6f0fa' : '#fff', 
                  border: isAssigned ? '1px solid #003366' : '1px solid #ccc', 
                  borderRadius: '6px', 
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <input 
                  type="checkbox" 
                  checked={isAssigned} 
                  onChange={() => handleToggleTeacher(teacher.id)}
                  disabled={isSaving}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px', fontWeight: isAssigned ? 'bold' : 'normal', color: isAssigned ? '#003366' : '#333' }}>
                  {teacher.name}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}